import {
  cardBrandChoiceFor,
  creditInstallmentOptions,
  isCardPaymentMethod,
  resolveFeeRule,
  validateReceiptRequest,
  type CardBrandChoice,
  type CreateReceiptRequest,
  type FeeRuleSet,
  type ReceiptPartInput,
} from '@valiosascrm/shared';
import type { PaymentMethod } from '@/services/cash-flow';
import { parseReaisToCents } from '../money';
import { NOTHING_RECORDED_RETRY, isTransactionTimeout } from '@/lib/api';

// Story 65.19 — contas do compositor de partes. Sem estado e sem rede: a tela só mostra o que
// sai daqui. Dinheiro é texto no campo e inteiro em centavos em toda conta. Os atalhos preenchem
// o que falta de cada cobrança, à vista; nenhum deles rateia valor por conta própria.

export interface ComposerObligation {
  transactionId: string;
  label: string;
  /** O que ainda falta receber desta cobrança, em centavos. */
  balance: number;
  revision: number;
}

export interface ComposerPart {
  id: string;
  source: 'payment_method' | 'patient_credit';
  method: PaymentMethod;
  amount: string;
  /** Valor entregue pela cliente; vazio = igual ao valor da forma. */
  received: string;
  installments: number;
  cardBrand: string;
  /** Story 65.20: máquina ou provedor da forma; vazio = não informado. */
  profileId: string;
  /** Data civil do pagamento; vazio = hoje. */
  date: string;
  dateReason: string;
  /** Quanto desta forma vai para cada cobrança. */
  allocations: Record<string, string>;
  excessKind: '' | 'patient_credit' | 'refund_tracking';
  excessReason: string;
}

export interface ComposerState {
  /** Identidade do pedido: a mesma em toda nova tentativa desta composição. */
  requestId: string;
  parts: ComposerPart[];
  partialReason: string;
}

export type ComposerField = 'parts' | 'amount' | 'received' | 'allocations' | 'excess' | 'date' | 'dateReason' | 'partialReason' | 'cardBrand';

export interface ComposerProblem {
  partId?: string;
  field: ComposerField;
  message: string;
}

export const centsOf = (text: string): number => Math.max(0, parseReaisToCents(text ?? ''));
export const textOf = (cents: number): string =>
  new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format((cents ?? 0) / 100);
const money = (cents: number) => `R$ ${textOf(cents)}`;

const allocatedTo = (parts: ComposerPart[], transactionId: string) =>
  parts.reduce((sum, part) => sum + centsOf(part.allocations[transactionId] ?? ''), 0);

/** O que falta de cada cobrança, sem contar a parte `exceptId`. */
function remainingBy(state: ComposerState, obligations: ComposerObligation[], exceptId?: string): Map<string, number> {
  const others = state.parts.filter((part) => part.id !== exceptId);
  return new Map(obligations.map((item) => [item.transactionId, Math.max(0, item.balance - allocatedTo(others, item.transactionId))]));
}

function filled(remaining: Map<string, number>, obligations: ComposerObligation[]) {
  const allocations = Object.fromEntries(obligations.map((item) => [item.transactionId, textOf(remaining.get(item.transactionId) ?? 0)]));
  const total = obligations.reduce((sum, item) => sum + (remaining.get(item.transactionId) ?? 0), 0);
  return { allocations, amount: textOf(total) };
}

const blank = (id: string): ComposerPart => ({
  id,
  source: 'payment_method',
  method: 'pix',
  amount: '0,00',
  received: '',
  installments: 1,
  cardBrand: '',
  profileId: '',
  date: '',
  dateReason: '',
  allocations: {},
  excessKind: '',
  excessReason: '',
});

export function newComposer(obligations: ComposerObligation[], requestId: string, nextId: () => string): ComposerState {
  return addPart({ requestId, parts: [], partialReason: '' }, obligations, nextId);
}

/** Nova forma já com o que falta de cada cobrança. */
export function addPart(state: ComposerState, obligations: ComposerObligation[], nextId: () => string): ComposerState {
  return { ...state, parts: [...state.parts, { ...blank(nextId()), ...filled(remainingBy(state, obligations), obligations) }] };
}

export function removePart(state: ComposerState, partId: string): ComposerState {
  return { ...state, parts: state.parts.filter((part) => part.id !== partId) };
}

export function updatePart(state: ComposerState, partId: string, patch: Partial<Omit<ComposerPart, 'id'>>): ComposerState {
  return { ...state, parts: state.parts.map((part) => (part.id === partId ? { ...part, ...patch } : part)) };
}

/** Com uma cobrança só, o valor da forma é a distribuição. Com várias, a distribuição é de quem digita. */
export function setPartAmount(state: ComposerState, obligations: ComposerObligation[], partId: string, text: string): ComposerState {
  const single = obligations.length === 1 ? { allocations: { [obligations[0].transactionId]: textOf(centsOf(text)) } } : {};
  return updatePart(state, partId, { amount: text, ...single });
}

export function setAllocation(state: ComposerState, partId: string, transactionId: string, text: string): ComposerState {
  const part = state.parts.find((item) => item.id === partId);
  if (!part) return state;
  return updatePart(state, partId, { allocations: { ...part.allocations, [transactionId]: text } });
}

/** Atalho "usar o restante": esta forma passa a cobrir o que ainda falta de cada cobrança. */
export function fillRemaining(state: ComposerState, obligations: ComposerObligation[], partId: string): ComposerState {
  return updatePart(state, partId, filled(remainingBy(state, obligations, partId), obligations));
}

/** Atalho "quitar saldo": acrescenta uma forma com o que falta. Sem saldo, não muda nada. */
export function settleBalance(state: ComposerState, obligations: ComposerObligation[], nextId: () => string): ComposerState {
  return totalsOf(state, obligations).remaining > 0 ? addPart(state, obligations, nextId) : state;
}

export interface PartView {
  applied: number;
  received: number | null;
  change: number;
  excess: number;
  allocated: number;
}

/** Dinheiro devolve a diferença como troco; os demais meios guardam como excedente. */
export function partView(part: ComposerPart): PartView {
  const applied = centsOf(part.amount);
  const allocated = Object.values(part.allocations).reduce((sum, value) => sum + centsOf(value), 0);
  if (part.source === 'patient_credit') return { applied, received: null, change: 0, excess: 0, allocated };
  const received = part.received.trim() ? centsOf(part.received) : applied;
  const difference = Math.max(0, received - applied);
  return { applied, received, change: part.method === 'cash' ? difference : 0, excess: part.method === 'cash' ? 0 : difference, allocated };
}

export interface ComposerTotals {
  perObligation: Array<{ transactionId: string; balance: number; allocated: number; remaining: number }>;
  applied: number;
  remaining: number;
  change: number;
  excess: number;
  creditUsed: number;
  /** Alguma cobrança que entra neste recebimento continua com saldo: exige o motivo. */
  partial: boolean;
  /** Saldo que fica nas cobranças que entram neste recebimento. */
  outstanding: number;
}

export function totalsOf(state: ComposerState, obligations: ComposerObligation[]): ComposerTotals {
  const perObligation = obligations.map((item) => {
    const allocated = allocatedTo(state.parts, item.transactionId);
    return { transactionId: item.transactionId, balance: item.balance, allocated, remaining: item.balance - allocated };
  });
  const views = state.parts.map((part) => ({ part, view: partView(part) }));
  const remaining = perObligation.reduce((sum, item) => sum + Math.max(0, item.remaining), 0);
  // Cobrança sem nenhum valor nesta composição não faz parte deste recebimento: pode ser
  // recebida depois, sem motivo. Parcial é a cobrança que recebe menos do que falta.
  const outstanding = perObligation.filter((item) => item.allocated > 0).reduce((sum, item) => sum + Math.max(0, item.remaining), 0);
  return {
    perObligation,
    applied: views.reduce((sum, item) => sum + item.view.applied, 0),
    remaining,
    change: views.reduce((sum, item) => sum + item.view.change, 0),
    excess: views.reduce((sum, item) => sum + item.view.excess, 0),
    creditUsed: views.filter((item) => item.part.source === 'patient_credit').reduce((sum, item) => sum + item.view.applied, 0),
    partial: outstanding > 0,
    outstanding,
  };
}

/** Quanto do crédito da cliente ainda cabe sugerir: o menor entre o crédito livre e o que falta. */
export function creditSuggestion(state: ComposerState, obligations: ComposerObligation[], creditBalance: number): number {
  const totals = totalsOf(state, obligations);
  return Math.max(0, Math.min(creditBalance - totals.creditUsed, totals.remaining));
}

/** Parte de crédito: preenche as cobranças na ordem da lista, até o valor confirmado. */
export function addCreditPart(state: ComposerState, obligations: ComposerObligation[], amount: number, nextId: () => string): ComposerState {
  const remaining = remainingBy(state, obligations);
  let left = amount;
  const allocations: Record<string, string> = {};
  for (const item of obligations) {
    const take = Math.min(left, remaining.get(item.transactionId) ?? 0);
    allocations[item.transactionId] = textOf(take);
    left -= take;
  }
  const part: ComposerPart = { ...blank(nextId()), source: 'patient_credit', amount: textOf(amount - left), allocations };
  return { ...state, parts: [...state.parts, part] };
}

/**
 * Story 65.20 — a bandeira só é pedida quando muda a taxa deste pagamento (existe regra de
 * bandeira que ganharia para a máquina e o parcelamento escolhidos).
 */
export function brandChoiceOf(part: ComposerPart, rules: FeeRuleSet | undefined): CardBrandChoice {
  if (!rules || part.source !== 'payment_method') return { decisive: false, brands: [] };
  return cardBrandChoiceFor(
    { paymentMethod: part.method, paymentMethodProfileId: part.profileId || null, installments: part.method === 'credit_card' ? Math.max(1, part.installments) : null },
    rules,
  );
}

/**
 * 65.57 (FIX-65.57-005): parcelas do crédito em que o servidor acha regra de taxa para esta
 * parte — a mesma `resolveFeeRule` do servidor, com a forma, a máquina, a bandeira e as parcelas.
 * Regra sem número de parcelas vale para todas. Sem as regras lidas, nada.
 */
export function creditInstallmentsWithRule(part: ComposerPart, rules: FeeRuleSet | undefined): number[] | undefined {
  if (!rules) return undefined;
  const listed = [...rules.v2, ...rules.legacy]
    .filter((rule) => rule.isActive && rule.paymentMethod === part.method)
    .map((rule) => rule.installments)
    .filter((count): count is number => typeof count === 'number');
  return creditInstallmentOptions(listed).filter((installments) =>
    resolveFeeRule(
      { paymentMethod: part.method, paymentMethodProfileId: part.profileId || null, cardBrand: part.cardBrand || null, installments },
      rules,
    ) !== null);
}

/** Monta o pedido da API. Com qualquer problema, não há pedido: a tela mostra onde corrigir. */
export function buildRequest(
  state: ComposerState,
  obligations: ComposerObligation[],
  today: string,
  feeRules?: FeeRuleSet,
): { request?: CreateReceiptRequest; problems: ComposerProblem[] } {
  const problems: ComposerProblem[] = [];
  if (!state.parts.length) return { request: undefined, problems: [{ field: 'parts', message: 'Adicione ao menos uma forma de pagamento.' }] };

  const totals = totalsOf(state, obligations);
  const parts: ReceiptPartInput[] = [];
  for (const part of state.parts) {
    const at = { partId: part.id };
    const view = partView(part);
    if (view.applied === 0) {
      problems.push({ ...at, field: 'amount', message: 'Informe o valor desta forma.' });
      continue;
    }
    const credit = part.source === 'patient_credit';
    if (!credit && part.received.trim() && centsOf(part.received) < view.applied) {
      problems.push({ ...at, field: 'received', message: 'O valor entregue precisa cobrir o valor da forma.' });
    }
    if (view.allocated !== view.applied) {
      const difference = view.applied - view.allocated;
      problems.push({
        ...at,
        field: 'allocations',
        message: difference > 0
          ? `Distribua ${money(difference)} entre as cobranças.`
          : `A distribuição passa ${money(-difference)} do valor desta forma.`,
      });
    }
    if (!credit && view.excess > 0 && !part.excessKind) {
      problems.push({ ...at, field: 'excess', message: `Diga o que fazer com ${money(view.excess)} que passou do valor: guardar como Em Haver ou devolver.` });
    }
    if (!credit && !part.cardBrand.trim() && brandChoiceOf(part, feeRules).decisive) {
      problems.push({ ...at, field: 'cardBrand', message: 'Escolha a bandeira: ela muda a taxa deste pagamento.' });
    }
    const dated = !credit && part.date && part.date !== today;
    if (dated && part.date > today) problems.push({ ...at, field: 'date', message: 'A data do pagamento não pode ser no futuro.' });
    else if (dated && part.dateReason.trim().length < 3) problems.push({ ...at, field: 'dateReason', message: 'Informe o motivo da data diferente de hoje.' });

    const allocations = obligations
      .map((item) => ({ transactionId: item.transactionId, amount: centsOf(part.allocations[item.transactionId] ?? '') }))
      .filter((item) => item.amount > 0);
    if (credit) {
      parts.push({ clientPartId: part.id, sourceType: 'patient_credit', appliedAmount: view.applied, allocations });
      continue;
    }
    parts.push({
      clientPartId: part.id,
      sourceType: 'payment_method',
      paymentMethod: part.method,
      ...(part.profileId ? { paymentMethodProfileId: part.profileId } : {}),
      ...(part.method === 'credit_card' ? { installments: Math.max(1, part.installments) } : {}),
      ...(isCardPaymentMethod(part.method) && part.cardBrand.trim() ? { cardBrand: part.cardBrand.trim() } : {}),
      ...(part.received.trim() ? { receivedAmount: centsOf(part.received) } : {}),
      appliedAmount: view.applied,
      ...(dated ? { effectiveDate: part.date, effectiveDateReason: part.dateReason.trim() } : {}),
      allocations,
      ...(view.excess > 0 && part.excessKind
        ? { excessResolution: { kind: part.excessKind, amount: view.excess, ...(part.excessReason.trim() ? { reason: part.excessReason.trim() } : {}) } }
        : {}),
    });
  }

  for (const item of totals.perObligation) {
    if (item.remaining < 0) {
      const label = obligations.find((obligation) => obligation.transactionId === item.transactionId)?.label ?? 'cobrança';
      const owner = state.parts.find((part) => centsOf(part.allocations[item.transactionId] ?? '') > 0);
      problems.push({ partId: owner?.id, field: 'allocations', message: `O valor distribuído passa ${money(-item.remaining)} do saldo de ${label}.` });
    }
  }
  if (totals.partial && state.partialReason.trim().length < 3) {
    problems.push({ field: 'partialReason', message: `Ficam ${money(totals.outstanding)} em aberto: informe o motivo do recebimento parcial.` });
  }
  if (problems.length) return { request: undefined, problems };

  const request: CreateReceiptRequest = {
    requestId: state.requestId,
    expectedTransactions: obligations
      .filter((item) => allocatedTo(state.parts, item.transactionId) > 0)
      .map((item) => ({ transactionId: item.transactionId, expectedRevision: item.revision })),
    parts,
    ...(totals.partial ? { partialReason: state.partialReason.trim() } : {}),
  };
  // As mesmas regras que a API aplica: o que escapar acima aparece aqui, antes de enviar.
  const violations = validateReceiptRequest(request);
  if (violations.length) {
    return { request: undefined, problems: violations.map((violation) => ({ partId: violation.clientPartId, field: 'parts' as const, message: violation.message })) };
  }
  return { request, problems: [] };
}

export interface ErrorNotice {
  message: string;
  /** As cobranças mudaram: a tela oferece atualizar, mantendo o que foi preenchido. */
  refresh: boolean;
  /** O identificador do pedido não serve mais: a próxima confirmação é um pedido novo. */
  newRequest: boolean;
}

/** Aviso para uma falha da confirmação. Em toda falha o que foi preenchido continua na tela. */
export function errorNotice(error: unknown): ErrorNotice {
  const response = (error as { response?: { status?: number; data?: { code?: string; message?: string } } })?.response;
  if (!response) {
    return {
      message: 'O recebimento não foi confirmado: o servidor não respondeu. Confirme de novo; o mesmo pedido não é gravado duas vezes.',
      refresh: false,
      newRequest: false,
    };
  }
  const { code, message } = response.data ?? {};
  // 65.33: nada foi gravado; o mesmo pedido segue valendo para a nova tentativa.
  if (isTransactionTimeout(error)) return { message: NOTHING_RECORDED_RETRY, refresh: false, newRequest: false };
  switch (code) {
    case 'RECEIPT_REVISION_CONFLICT':
    case 'RECEIPT_TRANSACTION_NOT_RECEIVABLE':
    case 'RECEIPT_OVER_ALLOCATION':
      return { message: `${message ?? 'A cobrança mudou.'} Atualize as cobranças e confira os valores; o que você preencheu continua aqui.`, refresh: true, newRequest: false };
    case 'RECEIPT_CREDIT_INSUFFICIENT':
      return { message: 'O crédito disponível da cliente não cobre mais o valor pedido. Atualize as cobranças para ver o crédito de agora.', refresh: true, newRequest: false };
    case 'RECEIPT_CLOSED_PERIOD':
      return { message: message ?? 'O caixa desta data está fechado. Reabra o caixa para correção antes de lançar.', refresh: false, newRequest: false };
    case 'RECEIPT_CARD_BRAND_REQUIRED':
    case 'RECEIPT_PROFILE_INVALID':
      return { message: `${message ?? 'As taxas da clínica mudaram.'} As máquinas e bandeiras foram atualizadas; confira esta forma e confirme de novo.`, refresh: true, newRequest: false };
    case 'IDEMPOTENCY_KEY_REUSED':
      return { message: 'Este pedido já foi enviado com outros valores. Confira o recebimento antes de confirmar de novo.', refresh: true, newRequest: true };
    default:
      return { message: message ?? 'Não foi possível confirmar o recebimento. Nada foi alterado.', refresh: false, newRequest: false };
  }
}
